import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";

// Left/right hero characters (tall fighter images from public/)
const LEFT_HERO  = "/28-boy.png";   // big strong male fighter
const RIGHT_HERO = "/34-girl.png";  // strong female fighter

function KpiBar({ label, score, max, notes }) {
  const pct   = max > 0 ? Math.round((score / max) * 100) : 0;
  const color = pct >= 70 ? "#22c55e" : pct >= 50 ? "#fbbf24" : "#e63946";
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 600 }}>
          {label}
        </span>
        <span style={{ color, fontWeight: 800, fontSize: 12, fontFamily: "monospace" }}>
          {score}/{max}
        </span>
      </div>
      <div style={{ height: 5, background: "rgba(255,255,255,0.07)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`, borderRadius: 4, background: color,
          boxShadow: `0 0 6px ${color}66`, transition: "width 1.2s ease-out",
        }} />
      </div>
      {notes && (
        <p style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, margin: "3px 0 0", lineHeight: 1.4 }}>
          {notes}
        </p>
      )}
    </div>
  );
}

function ScoreDonut({ score, max }) {
  const pct   = max > 0 ? score / max : 0;
  const r     = 44;
  const circ  = 2 * Math.PI * r;
  const dash  = pct * circ;
  const color = pct >= 0.7 ? "#22c55e" : pct >= 0.55 ? "#fbbf24" : "#e63946";
  return (
    <svg width={110} height={110}>
      <circle cx={55} cy={55} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={9} />
      <circle
        cx={55} cy={55} r={r} fill="none"
        stroke={color} strokeWidth={9}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 55 55)"
        style={{ transition: "stroke-dasharray 1.2s ease-out", filter: `drop-shadow(0 0 7px ${color})` }}
      />
      <text x={55} y={51} textAnchor="middle" fill="#f1f1f1" fontSize={20} fontWeight={900} fontFamily="monospace">{score}</text>
      <text x={55} y={67} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize={11}>of {max}</text>
    </svg>
  );
}

export default function MockFeedback() {
  const { sessionId } = useParams();
  const navigate      = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    api.mockSessionFeedback(sessionId)
      .then(setFeedback)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#060a1f" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#e63946", fontFamily: "monospace", marginBottom: 8 }}>Generating your feedback…</div>
          <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>This may take a moment</div>
        </div>
      </div>
    );
  }

  if (!feedback) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#060a1f" }}>
        <div style={{ color: "rgba(255,255,255,0.4)", fontFamily: "monospace" }}>Feedback not available yet.</div>
      </div>
    );
  }

  const {
    position_title, persona_name, verdict, total_score, total_max,
    kpi_scores, feedback_text, improvement_areas, strengths, duration_seconds, previous_attempts,
  } = feedback;

  const verdictMap = {
    shortlisted: { label: "Shortlisted ✅",  color: "#22c55e", bg: "rgba(34,197,94,0.08)",  border: "rgba(34,197,94,0.25)" },
    conditional: { label: "Conditional ⚠️",  color: "#fbbf24", bg: "rgba(251,191,36,0.08)", border: "rgba(251,191,36,0.25)" },
    rejected:    { label: "Not Selected ❌",  color: "#e63946", bg: "rgba(230,57,70,0.08)",  border: "rgba(230,57,70,0.25)" },
    pending:     { label: "Under Review 🔍",  color: "#60a5fa", bg: "rgba(96,165,250,0.08)", border: "rgba(96,165,250,0.25)" },
  };
  const vd  = verdictMap[verdict] || verdictMap.pending;
  const dur = Math.round((duration_seconds || 0) / 60);

  return (
    <div style={{
      height: "100vh", overflow: "hidden",
      background: "#060a1f", color: "#f1f1f1",
      fontFamily: "'Inter', sans-serif",
      display: "flex", flexDirection: "column",
      position: "relative",
    }}>
      <style>{`
        @keyframes hero-in-l    { from{opacity:0;transform:translateX(-50px)} to{opacity:1;transform:translateX(0)} }
        @keyframes hero-in-r    { from{opacity:0;transform:translateX(50px)}  to{opacity:1;transform:translateX(0)} }
        @keyframes hero-float   { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-10px)} }
        @keyframes hero-float-r { 0%,100%{transform:scaleX(-1) translateY(0px)} 50%{transform:scaleX(-1) translateY(-10px)} }
        @keyframes hero-scan    { 0%{top:-4px;opacity:0.7} 80%{opacity:0.7} 100%{top:100%;opacity:0} }
        @keyframes hero-glow    { 0%,100%{opacity:0.5;transform:scaleX(1)}   50%{opacity:1;transform:scaleX(1.15)} }
        @keyframes hero-ring    { 0%,100%{transform:scale(1);opacity:0.6}    50%{transform:scale(1.25);opacity:0.1} }
        @keyframes hero-particle{ 0%{transform:translateY(0) scale(1);opacity:0.9} 100%{transform:translateY(-100px) scale(0);opacity:0} }
        @keyframes fb-in        { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        ::-webkit-scrollbar{width:4px;background:transparent}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:4px}
      `}</style>

      {/* Background grid */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: "linear-gradient(rgba(230,57,70,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(230,57,70,0.03) 1px,transparent 1px)",
        backgroundSize: "55px 55px",
      }} />

      {/* ── LEFT HERO ── clipped to 30% left margin, pushed 5vh into ground */}
      <div style={{
        position: "absolute", bottom: "-5vh", left: 0, zIndex: 2,
        width: "30%", height: "105vh",
        overflow: "hidden", pointerEvents: "none",
        animation: "hero-in-l 0.9s cubic-bezier(0.22,1,0.36,1)",
      }}>
        {/* Red ground glow — anchored at the viewport base (5vh up from container bottom) */}
        <div style={{
          position: "absolute", bottom: "5vh", left: "-15%", right: "-15%", height: 120,
          background: "radial-gradient(ellipse at 50% 100%, rgba(230,57,70,0.75) 0%, rgba(230,57,70,0.35) 45%, transparent 70%)",
          animation: "hero-glow 2.8s ease-in-out infinite",
          zIndex: 3,
        }} />
        {/* Pulsing ring at feet */}
        <div style={{
          position: "absolute", bottom: "calc(5vh + 4px)", left: "5%", right: "5%", height: 18,
          borderRadius: "50%",
          boxShadow: "0 0 50px 20px rgba(230,57,70,0.55)",
          animation: "hero-ring 2.8s ease-in-out infinite",
          zIndex: 3,
        }} />
        {/* Hero image — anchored 5vh up from container bottom = sits on viewport floor */}
        <img src={LEFT_HERO} alt="" style={{
          position: "absolute", bottom: "5vh", left: 0, right: 0,
          height: "calc(100% - 5vh)", width: "100%",
          objectFit: "contain", objectPosition: "bottom center",
          filter: "brightness(0.92) drop-shadow(4px 0 24px rgba(230,57,70,0.55))",
          maskImage: "linear-gradient(to top, black 65%, rgba(0,0,0,0.5) 88%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to top, black 65%, rgba(0,0,0,0.5) 88%, transparent 100%)",
          animation: "hero-float 4.5s ease-in-out infinite",
        }} />
        {/* Red scan line sweeping down */}
        <div style={{
          position: "absolute", left: 0, right: 0, height: 2, zIndex: 4,
          background: "linear-gradient(90deg, transparent, rgba(230,57,70,0.8), transparent)",
          animation: "hero-scan 3s linear 1s infinite",
        }} />
        {/* Floating energy particles */}
        {[
          { left: "30%", delay: "0s"   },
          { left: "55%", delay: "1.2s" },
          { left: "70%", delay: "2.1s" },
          { left: "20%", delay: "3.0s" },
        ].map((p, i) => (
          <div key={i} style={{
            position: "absolute", bottom: "5vh", left: p.left,
            width: 5, height: 5, borderRadius: "50%",
            background: "#e63946", boxShadow: "0 0 8px rgba(230,57,70,1)",
            animation: `hero-particle 2.4s ease-out ${p.delay} infinite`,
            zIndex: 4,
          }} />
        ))}
        {/* Inner red edge glow */}
        <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 50, background: "linear-gradient(to left, rgba(230,57,70,0.12), transparent)" }} />
      </div>

      {/* ── RIGHT HERO ── clipped to 30% right margin, pushed 5vh into ground */}
      <div style={{
        position: "absolute", bottom: "-5vh", right: 0, zIndex: 2,
        width: "30%", height: "105vh",
        overflow: "hidden", pointerEvents: "none",
        animation: "hero-in-r 0.9s cubic-bezier(0.22,1,0.36,1)",
      }}>
        {/* Red ground glow */}
        <div style={{
          position: "absolute", bottom: "5vh", left: "-15%", right: "-15%", height: 120,
          background: "radial-gradient(ellipse at 50% 100%, rgba(230,57,70,0.75) 0%, rgba(230,57,70,0.35) 45%, transparent 70%)",
          animation: "hero-glow 2.8s ease-in-out 0.5s infinite",
          zIndex: 3,
        }} />
        {/* Pulsing ring at feet */}
        <div style={{
          position: "absolute", bottom: "calc(5vh + 4px)", left: "5%", right: "5%", height: 18,
          borderRadius: "50%",
          boxShadow: "0 0 50px 20px rgba(230,57,70,0.55)",
          animation: "hero-ring 2.8s ease-in-out 0.5s infinite",
          zIndex: 3,
        }} />
        {/* Hero image — anchored 5vh up from container bottom = sits on viewport floor */}
        <img src={RIGHT_HERO} alt="" style={{
          position: "absolute", bottom: "5vh", left: 0, right: 0,
          height: "calc(100% - 5vh)", width: "100%",
          objectFit: "contain", objectPosition: "bottom center",
          transform: "scaleX(-1)",
          filter: "brightness(0.92) drop-shadow(-4px 0 24px rgba(230,57,70,0.55))",
          maskImage: "linear-gradient(to top, black 65%, rgba(0,0,0,0.5) 88%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to top, black 65%, rgba(0,0,0,0.5) 88%, transparent 100%)",
          animation: "hero-float-r 4.5s ease-in-out 0.5s infinite",
        }} />
        {/* Red scan line sweeping down */}
        <div style={{
          position: "absolute", left: 0, right: 0, height: 2, zIndex: 4,
          background: "linear-gradient(90deg, transparent, rgba(230,57,70,0.8), transparent)",
          animation: "hero-scan 3s linear 2s infinite",
        }} />
        {/* Floating energy particles */}
        {[
          { left: "25%", delay: "0.5s" },
          { left: "50%", delay: "1.7s" },
          { left: "65%", delay: "2.6s" },
          { left: "75%", delay: "3.5s" },
        ].map((p, i) => (
          <div key={i} style={{
            position: "absolute", bottom: "5vh", left: p.left,
            width: 5, height: 5, borderRadius: "50%",
            background: "#e63946", boxShadow: "0 0 8px rgba(230,57,70,1)",
            animation: `hero-particle 2.4s ease-out ${p.delay} infinite`,
            zIndex: 4,
          }} />
        ))}
        {/* Inner red edge glow */}
        <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 50, background: "linear-gradient(to right, rgba(230,57,70,0.12), transparent)" }} />
      </div>

      {/* ── Header — matches all other pages ── */}
      <div style={{ position: "relative", zIndex: 10, flexShrink: 0 }}>
        <Header />
      </div>

      {/* Nav breadcrumb row */}
      <div style={{
        position: "relative", zIndex: 10, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0.4rem 1.5rem",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        background: "rgba(6,10,31,0.6)", backdropFilter: "blur(8px)",
      }}>
        <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 12 }}>Interview Feedback</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => navigate("/mock/positions")} style={{
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.55)", borderRadius: 7, padding: "3px 11px",
            fontSize: 11, cursor: "pointer",
          }}>← Positions</button>
          <button onClick={() => navigate("/mock/history")} style={{
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)",
            color: "rgba(255,255,255,0.55)", borderRadius: 7, padding: "3px 11px",
            fontSize: 11, cursor: "pointer",
          }}>My History</button>
        </div>
      </div>

      {/* ── Main content — strictly centre column, heroes in side margins ── */}
      <div style={{
        flex: 1, position: "relative", zIndex: 10,
        display: "grid",
        /* side columns = hero width (18%) | content | hero width (18%) */
        gridTemplateColumns: "30% 1fr 30%",
        overflow: "hidden",
      }}>
        {/* Left margin spacer */}
        <div />

        {/* Centre content */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "200px 1fr 1fr",
          gridTemplateRows: "auto 1fr",
          gap: "0.65rem",
          padding: "0.65rem 0.5rem",
          overflow: "hidden",
          animation: "fb-in 0.45s ease-out",
        }}>

          {/* ── Verdict banner: full width ── */}
          <div style={{
            gridColumn: "1 / -1",
            background: vd.bg, border: `1px solid ${vd.border}`,
            borderRadius: 12, padding: "0.7rem 1.1rem",
            display: "flex", alignItems: "center", gap: "1.25rem",
          }}>
            <ScoreDonut score={total_score || 0} max={total_max || 20} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
                <span style={{ color: vd.color, fontWeight: 900, fontSize: 17 }}>{vd.label}</span>
                <span style={{ color: "rgba(255,255,255,0.32)", fontSize: 11 }}>
                  {position_title} · by {persona_name} · {dur} min ·{" "}
                  {previous_attempts > 0 ? `Attempt #${previous_attempts + 1}` : "First attempt"}
                </span>
              </div>
              <p style={{ color: "rgba(255,255,255,0.62)", fontSize: 12, lineHeight: 1.55, margin: 0 }}>
                {feedback_text}
              </p>
            </div>
          </div>

          {/* ── KPI breakdown ── */}
          <div style={{
            background: "rgba(10,14,40,0.82)", border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 12, padding: "0.8rem", overflowY: "auto",
          }}>
            <div style={{ color: "#f1f1f1", fontWeight: 800, fontSize: 12, marginBottom: 10 }}>📊 KPI Breakdown</div>
            {Object.entries(kpi_scores || {}).map(([key, kpi]) => (
              <KpiBar key={key} label={kpi.label || key.replace(/_/g, " ")} score={kpi.score || 0} max={kpi.max || 5} notes={kpi.notes} />
            ))}
          </div>

          {/* ── Strengths ── */}
          <div style={{
            background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.18)",
            borderRadius: 12, padding: "0.8rem", overflowY: "auto",
          }}>
            <div style={{ color: "#22c55e", fontWeight: 800, fontSize: 12, marginBottom: 10 }}>💪 Strengths</div>
            {(strengths || []).map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 7, marginBottom: 7 }}>
                <span style={{ color: "#22c55e", flexShrink: 0, fontSize: 11 }}>✓</span>
                <span style={{ color: "rgba(255,255,255,0.68)", fontSize: 11.5, lineHeight: 1.5 }}>{s}</span>
              </div>
            ))}
          </div>

          {/* ── Improvements + CTA ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", overflow: "hidden" }}>
            <div style={{
              background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.18)",
              borderRadius: 12, padding: "0.8rem", flex: 1, overflowY: "auto",
            }}>
              <div style={{ color: "#fbbf24", fontWeight: 800, fontSize: 12, marginBottom: 10 }}>🎯 Areas to Improve</div>
              {(improvement_areas || []).map((area, i) => (
                <div key={i} style={{ marginBottom: 9 }}>
                  <div style={{ color: "#fbbf24", fontWeight: 700, fontSize: 11.5 }}>{area.area}</div>
                  <div style={{ color: "rgba(255,255,255,0.52)", fontSize: 11, lineHeight: 1.5, marginTop: 2 }}>{area.suggestion}</div>
                </div>
              ))}
            </div>
            <button onClick={() => navigate("/mock/positions")} style={{
              background: "linear-gradient(135deg,#e63946,#c1121f)",
              color: "#fff", border: "none", borderRadius: 10,
              padding: "9px 0", fontWeight: 700, fontSize: 12.5, cursor: "pointer",
              flexShrink: 0, letterSpacing: "0.03em",
              boxShadow: "0 4px 20px rgba(230,57,70,0.35)",
            }}>
              Try Another Mock →
            </button>
          </div>
        </div>

        {/* Right margin spacer */}
        <div />
      </div>
    </div>
  );
}
